import type { FC } from 'react';
import { useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { Url } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import ClockIcon from '@wix/da-ds/pkg/Icons/24x24/Clock';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { useDispatch } from 'react-redux';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import BatchEditOrDeleteConfirmation from '../../../../modals/BatchEditOrDeleteConfirmation';
import MenuSection from '../../_partials/MenuSection';
import SelectionArea from '../../_partials/SelectionArea';
import { StudioSection } from '../../../../types/store';
import { SelectionProps } from '../../types';
import BatchSellGhostButton from '../../_partials/BatchSellGhostButton';

import s from './SelectedLayout.scss';

export interface Props extends SelectionProps {
  activeSection: StudioSection;
  isUnpublishedDataset?: boolean;
  contentType?: string;
  onSubmit: () => void;
}

const SelectedLayout: FC<Props> = ({
  activeSection,
  isUnpublishedDataset,
  contentType,
  selectedCount,
  onSubmit,
  ...rest
}) => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const dispatch = useDispatch();
  const [isShowingConfirmation, setShowingConfirmation] = useState(false);
  const isMultiSelect = selectedCount > 1;
  const isPosts = contentType === 'posts';
  const isJournals = activeSection === StudioSection.JOURNALS;
  const isPublishedSection = activeSection === StudioSection.PUBLISHED;
  const showBatchSellButton = isMultiSelect && isPublishedSection && !isPosts;
  const canSubmit = hasCoreMembership || !isMultiSelect;

  const onScheduleClick = useCallback(() => {
    dispatch(
      pushModal(ModalType.STUDIO_BATCH_SCHEDULE, { params: { selectedCount } })
    );
  }, [dispatch, selectedCount]);

  return (
    <div className={s['root']}>
      <SelectionArea selectedCount={selectedCount} {...rest} />
      <div className={s['divider']} />
      <MenuSection
        className={s['menu']}
        activeSection={activeSection}
        selectedCount={selectedCount}
        contentType={contentType}
        withCoreSymbol={!canSubmit}
        isUnpublishedDataset={isUnpublishedDataset}
        onScheduleClick={onScheduleClick}
      />

      {isUnpublishedDataset && (
        <TextButton
          className={c(
            s['schedule'],
            !hasCoreMembership && s['schedule-noncore']
          )}
          variant="main"
          size="large"
          icon={ClockIcon}
          iconProps={{
            size: IconSize.TINY,
          }}
          onClick={onScheduleClick}
        >
          {t('studio.table.header.menuItem.edit.schedule.label')}
        </TextButton>
      )}

      {isUnpublishedDataset && canSubmit && (
        <BrandedButton
          variant="brand-green"
          size="medium"
          onClick={() => setShowingConfirmation(true)}
        >
          {t(
            isJournals
              ? 'studio.table.header.submitJournal'
              : 'studio.table.header.submitSelected',
            { count: selectedCount }
          )}
        </BrandedButton>
      )}
      {isUnpublishedDataset && !canSubmit && (
        <SimpleTooltip
          text={t('studio.table.header.submitNonCore.tooltip', {
            postProcess: 'reactPostprocessor',
            upgradeLink: (
              <PlainButtonOrLink
                className={s['tooltip-upgrade-link']}
                href={Url.coreMembershipLink()}
                target="_blank"
                biData={BiData<BuyCoreButtonBiEvent>({
                  evid: 375,
                  referral_info: ReferralType.STUDIO_MULTI_SUBMIT_TOOLTIP,
                })}
              >
                {t('studio.table.header.submitNonCore.upgradeLink')}
              </PlainButtonOrLink>
            ),
          })}
          usePortal
        >
          <BuyCoreBrandedButton
            size="medium"
            biData={BiData<BuyCoreButtonBiEvent>({
              evid: 375,
              referral_info: ReferralType.STUDIO_MULTI_SUBMIT,
            })}
          >
            {t('studio.table.header.submitNonCore')}
          </BuyCoreBrandedButton>
        </SimpleTooltip>
      )}

      {showBatchSellButton && <BatchSellGhostButton />}

      {!isUnpublishedDataset || canSubmit ? (
        <BatchEditOrDeleteConfirmation
          isOpen={isShowingConfirmation}
          selectedCount={selectedCount}
          onConfirm={onSubmit}
          onCancel={() => setShowingConfirmation(false)}
          variant="submit"
        />
      ) : null}
    </div>
  );
};
SelectedLayout.displayName = 'SelectedLayout';

export default SelectedLayout;
